import { NetworkStatus, useLazyQuery, useQuery } from '@apollo/client';
import IchiBalance from 'common/components/IchiBalance/IchiBalance';
import { QueryItemResponse } from 'common/models/graphql';
import { AppSyncToken } from 'common/models/token';
import { AppSyncUserInfo } from 'common/models/userInfo';
import { REFRESH_INTERVALS } from 'constants/constants';
import debounce from 'lodash/debounce';
import {
  getTokenQuery,
  GetTokenVariables,
} from 'graphql/queries/getToken.query';
import {
  getUserInfoQuery,
  GetUserInfoVariables,
} from 'graphql/queries/getUserInfo.query';
import React from 'react';
import useSelectedChain from 'hooks/useSelectedChain';
import { useAccount } from 'wagmi';

type Props = {
  isOnBottom?: boolean;
};

const IchiBalanceContainer: React.FC<Props> = (props) => {
  const { chainId } = useSelectedChain();
  const { address: accountAddress } = useAccount();

  const [
    loadUserInfo,
    {
      data: userInfo,
      networkStatus: networkStatusUserInfo,
      refetch: refetchUserInfo,
    },
  ] = useLazyQuery<QueryItemResponse<AppSyncUserInfo>, GetUserInfoVariables>(
    getUserInfoQuery,
    {
      // https://github.com/apollographql/react-apollo/issues/321
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'cache-and-network',
    }
  );

  const { data: ichiToken } = useQuery<
    QueryItemResponse<AppSyncToken>,
    GetTokenVariables
  >(getTokenQuery, {
    // Fetch ICHI info
    fetchPolicy: 'cache-and-network',
    variables: {
      name: 'ichi',
    },
    pollInterval: REFRESH_INTERVALS['getToken'],
  });
  const { data: ichiV2Token } = useQuery<
    QueryItemResponse<AppSyncToken>,
    GetTokenVariables
  >(getTokenQuery, {
    // Fetch ICHI info
    fetchPolicy: 'cache-and-network',
    variables: {
      name: 'ichi_v2',
    },
    pollInterval: REFRESH_INTERVALS['getToken'],
  });
  const { data: xichiToken } = useQuery<
    QueryItemResponse<AppSyncToken>,
    GetTokenVariables
  >(getTokenQuery, {
    // Fetch xICHI info
    fetchPolicy: 'cache-and-network',
    variables: {
      name: 'xichi',
    },
    pollInterval: REFRESH_INTERVALS['getToken'],
  });

  const executeRefetchUserInfo = async (): Promise<any> => {
    if (accountAddress && refetchUserInfo) {
      await refetchUserInfo({
        user: accountAddress,
        chainId: chainId,
      });
    }
  };
  const debouncedExecuteRefetchUserInfo = React.useMemo(
    () => debounce(executeRefetchUserInfo, 750),
    []
  ) as () => Promise<void>;

  const executeLoadUserInfo = async (): Promise<any> => {
    if (accountAddress) {
      await loadUserInfo({
        variables: {
          user: accountAddress,
          chainId: chainId,
        },
      });
    }
    return Promise.resolve();
  };

  React.useEffect(() => {
    if (chainId && accountAddress) {
      void executeLoadUserInfo();
    }
    void executeLoadUserInfo();
  }, [accountAddress, chainId]);

  const emptyToken: AppSyncToken = {
    name: '',
    fullName: '',
    displayName: '',
    address: '',
    price: 0,
    price_24h_change: 0,
    circulating: 0,
    supply: 0,
    chainId: 1,
    isOneToken: false,
  };
  const appSyncIchiToken: AppSyncToken = ichiToken?.getToken || emptyToken;
  const appSyncIchiV2Token: AppSyncToken = ichiV2Token?.getToken || emptyToken;
  const appSyncXIchiToken: AppSyncToken = xichiToken?.getToken || emptyToken;
  const appSyncUserInfo: AppSyncUserInfo = userInfo?.userInfo || {
    farms: [],
    tokens: [],
  };
  return (
    <IchiBalance
      ichi={appSyncIchiToken}
      ichiV2={appSyncIchiV2Token}
      xichi={appSyncXIchiToken}
      userInfo={appSyncUserInfo}
      userInfoLoading={networkStatusUserInfo === NetworkStatus.refetch}
      refetchUserInfo={debouncedExecuteRefetchUserInfo}
      isOnBottom={props.isOnBottom}
      chainId={chainId}
    />
  );
};

export default IchiBalanceContainer;
